/**
 * Every colour, radius, shadow and font Grokbot uses, named once.
 *
 * Values are the LifeScribe brand scales from src/theme/designTokens.ts (brand.test.ts checks
 * they still match), written out so a chip or box picks a name rather than inventing a hex.
 * Status dots are the one exception and stay in lib/selectors.ts (STATUS_COLORS) and
 * StatusDot.tsx.
 */

export const FONT = {
  display: '"Playfair Display", serif',
  heading: '"Montserrat", sans-serif',
  mono: '"Fira Code", ui-monospace, Menlo, monospace',
};

/** Strings, never numbers: the admin theme's numeric shape.borderRadius is 0, so numeric radii render square. */
export const RADIUS = { sm: '4px', md: '8px', lg: '12px', xl: '16px', pill: '9999px' };

export const SHADOW = { card: '0 2px 5px rgba(0,0,0,0.04)', hover: '0 4px 12px rgba(0,0,0,0.08)' };

/** Company accents: LifeScribe deep teal, Quill rich burgundy. */
export const ACCENT = {
  lifescribe: { acc: '#1A5F7A', soft: '#E6F2F5', ink: '#103A49' }, // deepTeal 500 / 50 / 700
  quill: { acc: '#7D1935', soft: '#F9E6EB', ink: '#5E1228' }, // burgundy main / 50 / dark
};

/** Semantic pairs. The only palette chips and status boxes may use. */
export const TONE = {
  good: { fg: '#0F4A3D', bg: '#E6F2F0', border: '#CCE5E0' }, // success dark / 50 / 100
  warn: { fg: '#7C6608', bg: '#FFF9E6', border: '#FFE799' }, // gold 700 / 50 / 200
  bad: { fg: '#5E1228', bg: '#F9E6EB', border: '#F3CCD6' }, // burgundy dark / 50 / 100
  info: { fg: '#144B5F', bg: '#E6F2F5', border: '#CCE5EA' }, // teal dark / 50 / 100
  neutral: { fg: '#4B5563', bg: '#F3F4F6', border: '#E5E7EB' }, // gray 600 / 100 / 200
  strong: { fg: '#FFFFFF', bg: '#111827', border: '#111827' }, // gray 900 (Blocker tag)
};
export type ToneName = keyof typeof TONE;

/** Heat scale, teal 50 → 500 (was blue). */
export const HEAT = ['#F3F4F6', '#CCE5EA', '#99CBD5', '#3397AB', '#1A5F7A'];

/** Primary teal for sparklines, trend bars and other single-series marks. */
export const TEAL = '#1A5F7A';
export const BURGUNDY = '#7D1935';
export const GOLD = '#D4AC0D';
/** Gold for borders/dots. Text uses WARN_TEXT (#A8890A was 3.4:1 on white; this is 5.6:1). */
export const GOLD_DARK = '#7C6608';
/** Warning text: dark gold on light (5.6:1), light gold on the dark theme (9.8:1). Use as an sx colour. */
export const WARN_TEXT = (t: { palette: { mode: string } }): string => (t.palette.mode === 'dark' ? '#E5BD1F' : '#7C6608');
/** Brand teal is unreadable as text on the dark background (2.5:1); the dark theme uses this tint (7.4:1). */
export const TEAL_ON_DARK = '#5FB3C7';
/** The dark theme's primary palette: light teal with dark text on filled controls (7.1:1). */
export const DARK_PRIMARY = { main: TEAL_ON_DARK, light: '#99CBD5', dark: '#3397AB', contrastText: '#0B1F27' };
/** Readable status words in the dark theme (the dots keep their fixed colours). */
export const GOOD_TEXT_ON_DARK = '#86D9A8';
export const BAD_TEXT_ON_DARK = '#F3A0B4';
/** Secondary text in the dark theme: the stock grey was 4.05:1 on the dark header strips; this is 5.6:1. */
export const TEXT_SECONDARY_ON_DARK = '#B8BFCA';
/** A company accent used as text: the accent in the light theme, a light tint of it in the dark one. */
export const accentText = (acc: string) => (t: { palette: { mode: string } }): string =>
  (t.palette.mode !== 'dark' ? acc : acc.toLowerCase() === '#7d1935' ? BAD_TEXT_ON_DARK : TEAL_ON_DARK);
/** Soft gold for text on the dark as-of bar. */
export const GOLD_SOFT = '#FFE799';

/** The sidebar wordmark header, reused by the as-of bar. Stays dark in both themes. */
export const HEADER_GRADIENT = 'linear-gradient(135deg,#0c3344 0%,#11465c 55%,#1A5F7A 100%)';
export const GOLD_UNDERLINE = 'linear-gradient(to right,#D4AC0D,transparent)';
export const ON_DARK_TEXT = 'rgba(249,243,233,.85)';

/** Focus ring that matches the sidebar's selected item. */
export const FOCUS_RING = `3px solid ${GOLD}`;

/** Overlines, cluster headers, table heads and palette kind labels (sizes stay with the caller). */
export const OVERLINE = { fontFamily: FONT.heading, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'text.secondary' } as const;
/** sx for a <TableHead>: every head cell in the overline style. */
export const TABLE_HEAD_SX = { '& .MuiTableCell-head': OVERLINE } as const;
/** KPI labels, as help/MetricCard. */
export const KPI_LABEL = { fontFamily: FONT.heading, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' } as const;
/** Clickable cards and KPI tiles: shadow, and a short lift on hover. */
export const CARD_LIFT = {
  boxShadow: SHADOW.card,
  transition: 'box-shadow .15s ease, transform .15s ease',
  '&:hover': { boxShadow: SHADOW.hover, transform: 'translateY(-2px)' },
} as const;
